/**
 * One day of the client's calendar: the number, the sessions on it, and a drop target.
 *
 * ── A refused target is NOT entered, and that is the refusal ────────────────────────────────
 * View ADR §11. `canDrop` answering `false` means the adapter never makes this cell the drop
 * target, so no indicator paints and a release over it lands nowhere. The reason reaches the
 * trainer through the keyboard path and the popover, where they asked the question explicitly.
 *
 * ── The CELL holds the grid's tab stop, and Enter steps into its sessions ───────────────────
 * The published grid pattern, and `StartMonthPicker`'s own: one stop for the whole grid, arrows
 * inside it. 52 weeks is 364 cells, and a tab stop on every chip made the footer 36 presses away on
 * a plan of five. Enter or Space on a cell moves focus to its first chip — where Space lifts,
 * the arrows aim and Escape hands the stop back to the cell.
 *
 * ── The chips SCROLL rather than being capped at two with a `+1` ────────────────────────────
 * A deliberate departure from the mock, recorded here because it is visible: the specimen draws two
 * chips and a tabular `+1`. A third session on one day is reachable that way only through a surface
 * that does not exist yet, and building one for a case a plan almost never has would be a whole
 * popover for a counter. The stack scrolls instead — fit-or-scroll, applied literally — so every
 * chip stays in the DOM, keeps its tab stop, and is scrolled into view when it takes focus.
 */

import { useRef, type ReactNode } from 'react';

import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';

import { CHIP_DRAG_KIND } from '../lib/useProjectionMove';
import type { ProjectionCell as Cell, ProjectionChip } from '../model/projectionGrid';

import styles from './AssignPlanDialog.module.css';

interface ProjectionCellProps {
  readonly cell: Cell;
  /**
   * Whether a drag may land on a day at all — `dropVerdict` for whatever is in the air.
   *
   * One stable function for the whole grid, taking the day as an argument: a closure per cell is
   * 364 of them rebuilt on every render, and every one of them tears down and re-registers the
   * adapter's drop target.
   */
  readonly canDrop: (chipKey: string, iso: string) => boolean;
  /** Whether a keyboard lift is currently aiming at this cell, and whether that aim is refused. */
  readonly aim: 'none' | 'allowed' | 'refused';
  /** This cell's address in the grid, which is what `useGridFocus` moves between. */
  readonly row: number;
  readonly column: number;
  /** Whether this cell currently holds the grid's one tab stop. */
  readonly focused: boolean;
  /** The cell itself taking focus, as opposed to one of its chips. */
  readonly onCellFocus: () => void;
  /** Focus moved INTO the chips — the grid's arrows must stand down while it is there. */
  readonly onChipFocus: () => void;
  readonly children: (chip: ProjectionChip) => ReactNode;
}

export function ProjectionCell({
  cell,
  canDrop,
  aim,
  row,
  column,
  focused,
  onCellFocus,
  onChipFocus,
  children,
}: ProjectionCellProps) {
  const element = useRef<HTMLDivElement>(null);

  const { isOver } = useDropTarget({
    ref: element,
    data: { iso: cell.iso },
    canDrop: (source) =>
      source.kind === CHIP_DRAG_KIND &&
      typeof source.chipKey === 'string' &&
      canDrop(source.chipKey, cell.iso),
  });

  return (
    <div
      ref={element}
      role="gridcell"
      aria-label={cell.label}
      data-cell={`${String(row)}-${String(column)}`}
      tabIndex={focused ? 0 : -1}
      onFocus={(event) => {
        // `focus` bubbles as `focusin` in React, so this fires for the chips too — and a chip
        // taking focus is exactly when the grid's arrows must stop answering, because they mean
        // something else inside a lift.
        if (event.target === event.currentTarget) onCellFocus();
        else onChipFocus();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const chip = element.current?.querySelector<HTMLElement>('[data-chip]');
        if (chip === null || chip === undefined) return;
        // The page scrolls on Space, and a grid that scrolls out from under the trainer is a grid
        // they have to find again.
        event.preventDefault();
        event.stopPropagation();
        chip.focus();
      }}
      className={[
        styles.cell,
        cell.beforeStart ? styles.cellBeforeStart : '',
        cell.isStart ? styles.cellStart : '',
        isOver || aim === 'allowed' ? styles.cellTarget : '',
        aim === 'refused' ? styles.cellRefused : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={styles.cellNumber}>{cell.dayOfMonth}</span>
      <div className={styles.cellChips}>{cell.chips.map((chip) => children(chip))}</div>
    </div>
  );
}
